import { useRef, useState } from 'react';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import Button from '../../../shared/components/Button';
import { CheckboxMultiSelect } from '../../charter/components/CheckboxMultiSelect';
import { useUploadPortfolioMedia } from '../hooks/usePortfolio';
import type { CreatePortfolioWorkPayload, PortfolioSolutionId, PortfolioWork } from '../types';

interface TagOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface UploadedMedia {
  id: string;
  cdnUrl: string;
}

interface Props {
  solutionId: PortfolioSolutionId;
  initial?: PortfolioWork | null;
  /** charter: booking-service services. atelier/rack-and-box: product variants. Resolved by the page. */
  tagOptions: TagOption[];
  tagOptionsLoading?: boolean;
  onSubmit: (values: CreatePortfolioWorkPayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}

/**
 * Create/edit form for a portfolio work, shared across all three solutions (PE-1831 follow-up).
 * Title/caption/media are identical everywhere; only the tagging picker's source and the
 * create/update payload's tag field differ — charter tags booking-service SERVICES,
 * atelier/rack-and-box tag product-service VARIANTS. `tagOptions` is resolved by the parent
 * page from the right source (useCharterServices vs useTaggableVariants) so this form stays
 * agnostic to where the options came from.
 */
export function PortfolioWorkForm({
  solutionId,
  initial,
  tagOptions,
  tagOptionsLoading,
  onSubmit,
  onCancel,
  isSaving,
}: Props) {
  const isCharter = solutionId === 'charter';

  const [title, setTitle] = useState(initial?.title ?? '');
  const [caption, setCaption] = useState(initial?.caption ?? '');
  const [tagIds, setTagIds] = useState<string[]>(
    isCharter ? (initial?.serviceIds ?? []) : (initial?.variantIds ?? []),
  );
  const [media, setMedia] = useState<UploadedMedia[]>(
    (initial?.media ?? []).map((m) => ({ id: m.id, cdnUrl: m.cdnUrl })),
  );
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = useUploadPortfolioMedia(solutionId);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      const result = await upload.mutateAsync(file);
      setMedia((prev) => [...prev, { id: result.id, cdnUrl: result.cdnUrl }]);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeMedia = (id: string) => setMedia((prev) => prev.filter((m) => m.id !== id));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (media.length === 0) {
      setError('Upload at least one image or video');
      return;
    }

    const base = { title: title.trim(), caption: caption.trim() || undefined, mediaIds: media.map((m) => m.id) };
    onSubmit(
      isCharter
        ? { ...base, serviceIds: tagIds.length > 0 ? tagIds : undefined }
        : { ...base, variantIds: tagIds },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input label="Title" placeholder="e.g. SS26 Ivory Bridal" value={title} onChange={(e) => setTitle(e.target.value)} />
      <TextArea label="Caption" value={caption} onChange={(e) => setCaption(e.target.value)} />

      <div>
        <label className="block text-sm text-foreground mb-1">Media</label>
        <div className="flex gap-2 flex-wrap mb-2">
          {media.map((m) => (
            <div key={m.id} className="relative">
              <img src={m.cdnUrl} alt="" className="h-20 w-20 rounded-md object-cover border border-border" />
              <button
                type="button"
                onClick={() => removeMedia(m.id)}
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white text-xs leading-none"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="text-sm text-muted-foreground"
        />
        {upload.isPending && <p className="text-xs text-muted-foreground mt-1">Uploading…</p>}
      </div>

      <CheckboxMultiSelect
        label={isCharter ? 'Tag services (optional)' : 'Tag product variants (optional)'}
        options={tagOptions}
        selected={tagIds}
        onChange={setTagIds}
        emptyLabel={
          tagOptionsLoading
            ? 'Loading…'
            : isCharter
              ? 'No services to tag yet.'
              : 'No product variants to tag yet.'
        }
      />

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-end gap-3 pt-1">
        <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" className="h-[40px]" disabled={isSaving || upload.isPending} loading={isSaving}>
          {initial ? 'Save changes' : 'Create work'}
        </Button>
      </div>
    </form>
  );
}
